/* eslint-disable vue/html-closing-bracket-newline */
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useMapGetter, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import TemplateEditorDialog from 'dashboard/routes/dashboard/campaigns/pages/templates/TemplateEditorDialog.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import ScheduledDateTimePicker from 'dashboard/components/ui/ScheduledDateTimePicker.vue';

const store = useStore();
const getters = useStoreGetters();
const { t: translate } = useI18n();
const t = (key, params) =>
  translate(key.replace('CAMPAIGN.WHATSAPP.BULK', 'CAMPAIGN.SMS.BULK'), params);

const isCreating = ref(false);
const editingTemplate = ref(null);
const selectedCampaignId = ref(null);
const reviewingCampaign = ref(null);
const selectedCampaign = computed({
  get: () =>
    getters['customMessageTemplates/getTemplates'].value.find(
      campaign => campaign.id === selectedCampaignId.value
    ) || null,
  set: campaign => {
    selectedCampaignId.value = campaign ? campaign.id : null;
  },
});
const activeTab = ref('active');
const scheduledAtCsv = ref('');
const scheduledAtLabel = ref('');

const currentUser = useMapGetter('getCurrentUser');
const isAdministrator = computed(
  () => currentUser.value?.role === 'administrator'
);

const closeCreateModal = () => {
  isCreating.value = false;
  editingTemplate.value = null;
};

const handleEdit = () => {
  editingTemplate.value = selectedCampaign.value;
  isCreating.value = true;
};

const openReview = campaign => {
  selectedCampaign.value = campaign;
  reviewingCampaign.value = campaign;
};

const closeReview = () => {
  reviewingCampaign.value = null;
};

const releaseReview = async () => {
  if (!reviewingCampaign.value || !isAdministrator.value) return;

  try {
    await store.dispatch(
      'customMessageTemplates/releaseReview',
      reviewingCampaign.value.id
    );
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.RELEASE_SUCCESS'));
    closeReview();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.BULK.ALERT_META_DELETE_ERROR')
    );
  }
};

const removeReview = async () => {
  if (!reviewingCampaign.value || !isAdministrator.value) return;
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(t('CAMPAIGN.WHATSAPP.BULK.CONFIRM_ARCHIVE'))
  ) {
    return;
  }

  try {
    await store.dispatch(
      'customMessageTemplates/destroyReview',
      reviewingCampaign.value.id
    );
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.DELETE_MODEL'));
    closeReview();
    selectedCampaign.value = null;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.BULK.ALERT_META_DELETE_ERROR')
    );
  }
};

const showDeleteModal = ref(false);

const closeDeleteModal = () => {
  showDeleteModal.value = false;
};

const confirmDeleteCampaign = async () => {
  if (!selectedCampaign.value) return;
  try {
    const campaignId = selectedCampaign.value.id;
    await store.dispatch('customMessageTemplates/delete', campaignId);
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_ARCHIVED'));
    selectedCampaign.value = null;
    closeDeleteModal();
  } catch (error) {
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_ARCHIVE_ERROR'));
  }
};

const confirmRestoreCampaign = async () => {
  if (!selectedCampaign.value) return;
  if (selectedCampaign.value.variables?.meta_deleted_at) {
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_META_DELETED'));
    return;
  }
  try {
    const campaignId = selectedCampaign.value.id;
    await store.dispatch('customMessageTemplates/restore', campaignId);
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_RESTORED'));
    selectedCampaign.value = null;
  } catch (error) {
    const errorMessage =
      error?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP.BULK.ALERT_RESTORE_ERROR');
    useAlert(errorMessage);
  }
};

const deleteCampaignFromMeta = async () => {
  if (!selectedCampaign.value?.variables?.meta_managed) return;
  if (
    // Native confirmation is required before permanently deleting the Meta template.
    // eslint-disable-next-line no-alert
    !window.confirm(t('CAMPAIGN.WHATSAPP.BULK.DELETE_META'))
  ) {
    return;
  }

  try {
    await store.dispatch(
      'customMessageTemplates/deleteFromMeta',
      selectedCampaign.value.id
    );
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_META_DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CAMPAIGN.WHATSAPP.BULK.ALERT_META_DELETE_ERROR')
    );
  }
};

const accountId = useMapGetter('getCurrentAccountId');
const uiFlags = useMapGetter('customMessageTemplates/getUIFlags');
const labels = useMapGetter('labels/getLabels');
const inboxes = useMapGetter('inboxes/getSendableInboxes');
const isFetchingCampaigns = computed(() => uiFlags.value.isFetching);

const whatsappCampaigns = computed(() => {
  let list = [];
  if (activeTab.value === 'active') {
    list = getters['customMessageTemplates/getActiveTemplates'].value || [];
  } else {
    list = getters['customMessageTemplates/getArchivedTemplates'].value || [];
  }

  // Esconder mensagens que foram criadas/copiadas EXCLUSIVAMENTE na tela de Mensagens Ativas
  return list.filter(tpl => {
    const isExclusive =
      tpl.variables?.is_exclusive_active_message === true ||
      tpl.variables?.is_exclusive_active_message === 'true';
    return !isExclusive;
  });
});

const webhookTriggerUrl = ref('');

const minDateTime = computed(() => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 16);
});

const testPhoneNumber = ref('');
const testMediaUrl = ref('');
const isTesting = ref(false);
const testResult = ref(null);
const testVariables = ref([{ key: '', value: '' }]);
const webhookExampleBody = computed(() => {
  const variables = {};
  testVariables.value.forEach(variable => {
    if (variable.key) variables[variable.key] = variable.value || 'Valor';
  });

  return JSON.stringify({ phone_number: '5531999999999', variables }, null, 2);
});

const hasDynamicMedia = computed(() => {
  const url = selectedCampaign.value?.variables?.headerUrl || '';
  return url.includes('{{');
});

const selectedFile = ref(null);
const isUploading = ref(false);
const uploadMessage = ref('');

const selectedLabel = ref('');
const isTriggeringLabel = ref(false);
const labelMessage = ref('');

const selectCampaign = async campaign => {
  selectedCampaign.value = campaign;
  testResult.value = null;
  selectedFile.value = null;
  uploadMessage.value = '';
  selectedLabel.value = '';
  labelMessage.value = '';
  scheduledAtCsv.value = '';
  scheduledAtLabel.value = '';
  testMediaUrl.value = campaign?.variables?.headerUrl || '';
  webhookTriggerUrl.value = '';

  if (campaign) {
    try {
      const { data } = await window.axios.get(
        `/api/v1/accounts/${accountId.value}/whatsapp_settings/${campaign.id}/webhook`
      );
      webhookTriggerUrl.value = data.url;
    } catch {
      useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_WEBHOOK'));
    }
  }

  const variableKeys = new Set();
  const extractVars = str => {
    if (!str) return;
    const matches = str.match(/{{(?:.*?=)?\s*([^}]+)\s*}}/g);
    if (matches) {
      matches.forEach(m => {
        let key = m.replace(/[{}]/g, '').trim();
        if (key.includes('=')) {
          key = key.split('=')[1].trim();
        }
        variableKeys.add(key);
      });
    }
  };

  if (campaign) {
    extractVars(campaign.content);
    if (campaign.variables?.footer) extractVars(campaign.variables.footer);
    if (campaign.buttons) {
      campaign.buttons.forEach(btn => {
        extractVars(btn.label);
        extractVars(btn.url);
      });
    }
    if (campaign.variables?.carouselItems) {
      campaign.variables.carouselItems.forEach(item => {
        extractVars(item.title);
        extractVars(item.description);
        extractVars(item.url);
      });
    }
    if (campaign.variables?.listItems) {
      campaign.variables.listItems.forEach(item => {
        extractVars(item.title);
        extractVars(item.description);
      });
    }
  }

  if (variableKeys.size > 0) {
    testVariables.value = Array.from(variableKeys).map(key => ({
      key,
      value: '',
    }));
  } else {
    testVariables.value = [];
  }
};

const copyWebhookUrl = () => {
  navigator.clipboard.writeText(webhookTriggerUrl.value);
  useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_COPIED'));
};

const downloadExampleCsv = () => {
  let headers = ['telefone'];
  let sampleData = ['5531999999999'];

  testVariables.value.forEach(v => {
    if (v.key) {
      headers.push(v.key);
      sampleData.push('exemplo');
    }
  });

  const csvContent = [headers.join(','), sampleData.join(',')].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute(
    'download',
    `modelo_disparo_${selectedCampaign.value?.id || 'whatsapp'}.csv`
  );
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handleFileUpload = event => {
  selectedFile.value = event.target.files[0];
};

const uploadCSV = async () => {
  if (
    scheduledAtCsv.value &&
    new Date(scheduledAtCsv.value) < new Date(minDateTime.value)
  ) {
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_PAST'));
    return;
  }

  if (!selectedFile.value) return;
  isUploading.value = true;
  uploadMessage.value = '';

  const formData = new FormData();
  formData.append('file', selectedFile.value);
  if (scheduledAtCsv.value) {
    formData.append('scheduled_at', scheduledAtCsv.value);
  }

  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/whatsapp_settings/${selectedCampaign.value.id}/bulk_trigger`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    uploadMessage.value = t('CAMPAIGN.WHATSAPP.BULK.UPLOAD_SUCCESS', {
      count: response.data.processed_count || 0,
    });
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_QUEUE_SUCCESS'));
  } catch (error) {
    useAlert(
      t('CAMPAIGN.WHATSAPP.BULK.ALERT_ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isUploading.value = false;
  }
};

const triggerByLabel = async () => {
  if (
    scheduledAtLabel.value &&
    new Date(scheduledAtLabel.value) < new Date(minDateTime.value)
  ) {
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_PAST'));
    return;
  }

  if (!selectedLabel.value) return;
  isTriggeringLabel.value = true;
  labelMessage.value = '';

  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/whatsapp_settings/${selectedCampaign.value.id}/trigger_by_label`,
      { label_name: selectedLabel.value, scheduled_at: scheduledAtLabel.value }
    );
    labelMessage.value = t('CAMPAIGN.WHATSAPP.BULK.UPLOAD_SUCCESS', {
      count: response.data.processed_count || 0,
    });
    useAlert(t('CAMPAIGN.WHATSAPP.BULK.ALERT_SEND_SUCCESS'));
  } catch (error) {
    useAlert(
      t('CAMPAIGN.WHATSAPP.BULK.ALERT_ERROR', {
        error: error.response?.data?.error || error.message,
      })
    );
  } finally {
    isTriggeringLabel.value = false;
  }
};

const testWebhook = async () => {
  isTesting.value = true;
  testResult.value = null;

  const payloadVariables = testVariables.value.reduce((acc, curr) => {
    if (curr.key.trim() !== '') acc[curr.key] = curr.value;
    return acc;
  }, {});

  try {
    const response = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/whatsapp_settings/${selectedCampaign.value.id}/trigger`,
      {
        phone_number: testPhoneNumber.value.replace(/\D/g, ''),
        media_url: testMediaUrl.value,
        variables: payloadVariables,
      }
    );
    testResult.value = JSON.stringify(response.data, null, 2);
  } catch (error) {
    testResult.value = `[ERROR] ${error.response?.data?.error || error.message}`;
  } finally {
    isTesting.value = false;
  }
};

const syncCampaignStatuses = async () => {
  const officialCampaigns = whatsappCampaigns.value.filter(
    campaign => campaign.inbox_id
  );

  await officialCampaigns.reduce(
    (promise, campaign) =>
      promise.then(() =>
        store
          .dispatch('customMessageTemplates/syncStatus', campaign.id)
          .catch(() => null)
      ),
    Promise.resolve()
  );
};

onMounted(async () => {
  await store.dispatch('customMessageTemplates/get');
  await syncCampaignStatuses();
});
</script>

<template>
  <!-- eslint-disable no-alert, vue/html-closing-bracket-newline -->
  <div
    class="flex flex-col h-full bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100 overflow-hidden w-full font-sans transition-colors duration-200"
  >
    <!-- Header Area -->
    <div
      class="z-10 flex flex-col border-b border-slate-200 bg-white px-4 pb-4 pt-5 shadow-sm dark:border-slate-700 dark:bg-slate-800 sm:px-8 sm:pt-8"
    >
      <div
        class="flex flex-col items-stretch gap-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <h1
            class="text-2xl font-semibold text-slate-800 dark:text-white tracking-tight"
          >
            {{ t('CAMPAIGN.WHATSAPP.BULK.TITLE') }}
          </h1>
          <p class="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {{ t('CAMPAIGN.WHATSAPP.BULK.DESCRIPTION') }}
          </p>
        </div>
        <div class="w-full sm:w-auto">
          <button
            class="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 sm:w-auto"
            @click="isCreating = true"
          >
            <span class="icon i-lucide-plus" />
            {{ t('CAMPAIGN.WHATSAPP.BULK.CREATE') }}
          </button>
        </div>
      </div>
    </div>

    <div
      class="flex flex-1 min-h-0 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden"
    >
      <!-- Left Sidebar: Campaigns List -->
      <div
        class="flex w-full shrink-0 flex-col border-b border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800 lg:h-full lg:w-[350px] lg:border-b-0 lg:border-r"
      >
        <div class="p-4 border-b border-slate-100 dark:border-slate-700/50">
          <h3
            class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
          >
            {{ t('CAMPAIGN.WHATSAPP.BULK.YOUR_CAMPAIGNS') }}
          </h3>
        </div>

        <div
          class="flex items-center p-3 border-b border-slate-200 dark:border-slate-700/50 bg-white dark:bg-slate-800"
        >
          <div
            class="flex bg-slate-100 dark:bg-slate-900/50 p-1 rounded-lg w-full"
          >
            <button
              class="flex-1 text-sm font-medium py-1.5 rounded-md transition-colors"
              :class="
                activeTab === 'active'
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              "
              @click="activeTab = 'active'"
            >
              {{ t('CAMPAIGN.WHATSAPP.BULK.ACTIVE') }}
            </button>
            <button
              class="flex-1 text-sm font-medium py-1.5 rounded-md transition-colors"
              :class="
                activeTab === 'archived'
                  ? 'bg-white dark:bg-slate-700 shadow-sm text-slate-800 dark:text-slate-200'
                  : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
              "
              @click="activeTab = 'archived'"
            >
              {{ t('CAMPAIGN.WHATSAPP.BULK.ARCHIVED') }}
            </button>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto p-3">
          <div v-if="isFetchingCampaigns" class="flex justify-center p-8">
            <span
              class="icon i-lucide-loader animate-spin text-2xl text-slate-400"
            />
          </div>
          <div
            v-else-if="whatsappCampaigns.length === 0"
            class="flex flex-col items-center justify-center p-8 text-center"
          >
            <div
              class="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center mb-3"
            >
              <span
                class="icon i-lucide-message-square text-xl text-slate-400"
              />
            </div>
            <p class="text-sm text-slate-500 dark:text-slate-400">
              {{ t('CAMPAIGN.WHATSAPP.BULK.NO_CAMPAIGNS') }}
            </p>
            <p class="text-xs text-slate-400 dark:text-slate-500 mt-1">
              {{ t('CAMPAIGN.WHATSAPP.BULK.NO_CAMPAIGNS_HINT') }}
            </p>
          </div>
          <ul v-else class="space-y-2">
            <li
              v-for="campaign in whatsappCampaigns"
              :key="campaign.id"
              role="button"
              tabindex="0"
              :aria-pressed="selectedCampaign?.id === campaign.id"
              class="group p-4 rounded-xl cursor-pointer transition-all border shadow-sm"
              :class="[
                selectedCampaign?.id === campaign.id
                  ? 'bg-blue-50 border-blue-200 dark:bg-blue-900/30 dark:border-blue-800'
                  : 'bg-white border-slate-200 hover:border-blue-300 dark:bg-slate-800 dark:border-slate-700 dark:hover:border-slate-600',
              ]"
              @click="selectCampaign(campaign)"
              @keydown.enter="selectCampaign(campaign)"
              @keydown.space.prevent="selectCampaign(campaign)"
            >
              <div class="flex items-start justify-between">
                <h4
                  class="font-medium text-sm truncate pr-2"
                  :class="
                    selectedCampaign?.id === campaign.id
                      ? 'text-blue-800 dark:text-blue-300'
                      : 'text-slate-800 dark:text-slate-200'
                  "
                >
                  {{ campaign.name }}
                </h4>
                <div
                  v-if="
                    campaign.inbox_id &&
                    inboxes.find(i => i.id === campaign.inbox_id)
                  "
                  class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400"
                >
                  {{ inboxes.find(i => i.id === campaign.inbox_id).name }}
                </div>
              </div>

              <div class="flex items-center justify-between mt-2">
                <p
                  class="text-xs text-slate-500 dark:text-slate-400 capitalize truncate mb-[2px]"
                >
                  {{
                    campaign.category === 'utility'
                      ? t('CAMPAIGN.WHATSAPP.BULK.UTILITY')
                      : campaign.category === 'authentication'
                        ? t('CAMPAIGN.WHATSAPP.BULK.AUTHENTICATION')
                        : t('CAMPAIGN.WHATSAPP.BULK.MARKETING')
                  }}
                </p>
                <div
                  v-if="campaign.status === 'awaiting_approval'"
                  class="flex items-center gap-2"
                >
                  <span
                    class="inline-flex h-6 items-center shrink-0 text-[10px] font-medium px-2 rounded bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900/70 dark:text-yellow-200 dark:border-yellow-700"
                  >
                    {{ t('CAMPAIGN.WHATSAPP.BULK.STATUS_AWAITING_APPROVAL') }}
                  </span>
                </div>
                <div
                  v-else-if="campaign.status === 'approved'"
                  class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP.BULK.STATUS_APPROVED') }}
                </div>
                <div
                  v-else-if="campaign.status === 'rejected'"
                  class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP.BULK.STATUS_REJECTED') }}
                </div>
                <div
                  v-else
                  class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-400"
                >
                  {{ t('CAMPAIGN.WHATSAPP.BULK.STATUS_PENDING') }}
                </div>
              </div>
              <div
                v-if="
                  campaign.status === 'awaiting_approval' && isAdministrator
                "
                class="mt-3 flex justify-end"
              >
                <button
                  type="button"
                  class="inline-flex h-7 items-center rounded-md bg-amber-500 px-3 text-xs font-semibold text-white shadow-sm hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400"
                  @click.stop="openReview(campaign)"
                >
                  {{ t('CAMPAIGN.WHATSAPP.BULK.REVIEW') }}
                </button>
              </div>
            </li>
          </ul>
        </div>
      </div>

      <!-- Right Panel: Config and Testing -->
      <div class="min-w-0 flex-1 overflow-y-auto bg-slate-50 dark:bg-slate-900">
        <div
          v-if="!selectedCampaign"
          class="flex flex-col items-center justify-center h-full text-center p-8"
        >
          <div
            class="w-20 h-20 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-6 border border-slate-200 dark:border-slate-700 shadow-sm"
          >
            <span class="icon i-lucide-smartphone text-3xl text-slate-400" />
          </div>
          <h2
            class="text-xl font-medium text-slate-800 dark:text-slate-200 mb-2"
          >
            {{ t('CAMPAIGN.WHATSAPP.BULK.PANEL_TITLE') }}
          </h2>
          <p class="text-slate-500 dark:text-slate-400 max-w-md">
            {{ t('CAMPAIGN.WHATSAPP.BULK.PANEL_DESCRIPTION') }}
          </p>
        </div>

        <div v-else class="mx-auto max-w-4xl space-y-8 p-4 sm:p-6 lg:p-8">
          <!-- Template Preview -->
          <div
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between gap-3"
            >
              <div class="flex items-center gap-3">
                <span class="icon i-lucide-file-text text-blue-500" />
                <h3 class="font-medium text-slate-800 dark:text-slate-200">
                  {{
                    t('CAMPAIGN.WHATSAPP.BULK.CAMPAIGN', {
                      name: selectedCampaign.name,
                    })
                  }}
                </h3>
              </div>
              <div class="flex gap-2">
                <button
                  v-if="
                    selectedCampaign.archived &&
                    !selectedCampaign.variables?.meta_deleted_at
                  "
                  class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                  :title="t('CAMPAIGN.WHATSAPP.BULK.RESTORE')"
                  @click="confirmRestoreCampaign"
                >
                  <span class="icon i-lucide-undo" />
                </button>
                <button
                  v-if="
                    selectedCampaign.archived &&
                    selectedCampaign.variables?.meta_managed &&
                    !selectedCampaign.variables?.meta_deleted_at
                  "
                  class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                  :title="t('CAMPAIGN.WHATSAPP.BULK.DELETE_META')"
                  @click="deleteCampaignFromMeta"
                >
                  <span class="icon i-lucide-trash-2" />
                </button>
                <span
                  v-if="selectedCampaign.variables?.meta_deleted_at"
                  class="text-xs font-medium text-red-500"
                >
                  {{ t('CAMPAIGN.WHATSAPP.BULK.DELETED_META') }}
                </span>
                <template v-else>
                  <button
                    class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    :title="t('CAMPAIGN.WHATSAPP.BULK.EDIT')"
                    @click="handleEdit"
                  >
                    <span class="icon i-lucide-pencil" />
                  </button>
                  <button
                    class="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors"
                    :title="t('CAMPAIGN.WHATSAPP.BULK.ARCHIVE')"
                    @click="showDeleteModal = true"
                  >
                    <span class="icon i-lucide-archive" />
                  </button>
                </template>
              </div>
            </div>
            <div class="p-6">
              <p
                class="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed"
              >
                {{ t('CAMPAIGN.WHATSAPP.BULK.CONFIGURED') }}
              </p>
            </div>
          </div>

          <!-- Webhook Section -->
          <div
            v-if="selectedCampaign.status === 'approved'"
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center"
                >
                  <span
                    class="icon i-lucide-webhook text-blue-600 dark:text-blue-400"
                  />
                </div>
                <div>
                  <h3 class="font-medium text-slate-800 dark:text-slate-200">
                    {{ t('CAMPAIGN.WHATSAPP.BULK.WEBHOOK_TITLE') }}
                  </h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ t('CAMPAIGN.WHATSAPP.BULK.WEBHOOK_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
            <div class="p-6">
              <div class="mb-2 flex flex-col items-start gap-3 sm:flex-row">
                <input
                  type="text"
                  readonly
                  :aria-label="t('CAMPAIGN.WHATSAPP.BULK.WEBHOOK_TITLE')"
                  :value="webhookTriggerUrl"
                  class="box-border h-10 min-h-10 max-h-10 min-w-0 flex-1 appearance-none rounded-lg border border-slate-200 bg-slate-50 px-4 py-2 text-sm leading-6 text-slate-600 focus:outline-none dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                />
                <button
                  class="box-border inline-flex h-10 min-h-10 max-h-10 w-full shrink-0 items-center justify-center gap-2 rounded-lg bg-blue-50 px-4 py-2 text-sm font-medium leading-6 text-blue-600 transition-colors hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50 sm:w-auto"
                  @click="copyWebhookUrl"
                >
                  <span class="icon i-lucide-copy" />
                  {{ t('CAMPAIGN.WHATSAPP.BULK.COPY_URL') }}
                </button>
              </div>
              <div
                class="overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-xs text-slate-300"
              >
                <div class="text-emerald-400">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.METHOD_POST') }}
                  {{ webhookTriggerUrl }}
                </div>
                <div class="mt-4 text-yellow-400">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.HEADERS') }}
                </div>
                <div>
                  {{ t('CAMPAIGN.WHATSAPP.BULK.CONTENT_TYPE') }}
                  <span class="text-slate-400">{{
                    t('CAMPAIGN.WHATSAPP.BULK.JSON_CONTENT_TYPE')
                  }}</span>
                </div>
                <div class="mt-4 text-blue-400">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.BODY') }}
                </div>
                <pre class="mt-1 whitespace-pre">{{ webhookExampleBody }}</pre>
              </div>
            </div>
          </div>

          <!-- CSV Import Section -->
          <div
            v-if="selectedCampaign.status === 'approved'"
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex justify-between items-center"
            >
              <div class="flex items-center gap-3">
                <div
                  class="w-8 h-8 rounded bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center"
                >
                  <span
                    class="icon i-lucide-file-spreadsheet text-emerald-600 dark:text-emerald-400"
                  />
                </div>
                <div>
                  <h3 class="font-medium text-slate-800 dark:text-slate-200">
                    {{ t('CAMPAIGN.WHATSAPP.BULK.CSV_TITLE') }}
                  </h3>
                  <p class="text-xs text-slate-500 dark:text-slate-400">
                    {{ t('CAMPAIGN.WHATSAPP.BULK.CSV_DESCRIPTION') }}
                  </p>
                </div>
              </div>
            </div>
            <div class="p-6">
              <div class="flex items-center justify-center w-full mb-4">
                <label
                  class="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 dark:hover:bg-bray-800 dark:bg-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:hover:border-slate-500 dark:hover:bg-slate-600 transition-colors"
                >
                  <div
                    class="flex flex-col items-center justify-center pt-5 pb-6"
                  >
                    <svg
                      class="w-8 h-8 mb-3 text-slate-500 dark:text-slate-400"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p class="mb-2 text-sm text-slate-500 dark:text-slate-400">
                      <span class="font-semibold">{{
                        selectedFile
                          ? selectedFile.name
                          : t('CAMPAIGN.WHATSAPP.BULK.SELECT_FILE')
                      }}</span>
                    </p>
                    <p
                      v-if="!selectedFile"
                      class="text-xs text-slate-500 dark:text-slate-400"
                    >
                      {{ t('CAMPAIGN.WHATSAPP.BULK.CSV_FORMAT') }}
                    </p>
                  </div>
                  <input
                    type="file"
                    accept=".csv"
                    :aria-label="t('CAMPAIGN.WHATSAPP.BULK.SELECT_FILE')"
                    class="hidden"
                    @change="handleFileUpload"
                  />
                </label>
              </div>

              <div
                class="relative mt-8 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div class="flex flex-col relative">
                  <button
                    class="flex h-[38px] w-fit items-center gap-1 text-sm font-medium text-blue-600 transition-colors hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                    @click="downloadExampleCsv"
                  >
                    <span class="icon i-lucide-download" />
                    {{ t('CAMPAIGN.WHATSAPP.BULK.DOWNLOAD_CSV') }}
                  </button>
                  <span
                    v-if="uploadMessage"
                    class="text-sm font-medium text-emerald-600 dark:text-emerald-400 absolute top-full mt-2 left-0 whitespace-nowrap"
                    >{{ uploadMessage }}</span
                  >
                </div>
                <div class="flex flex-wrap items-start gap-3">
                  <div class="relative flex h-[38px] flex-col">
                    <label
                      for="whatsapp-csv-scheduled-at"
                      class="text-xs text-slate-500 dark:text-slate-400 absolute -top-6 left-1 whitespace-nowrap"
                      >{{
                        t('CAMPAIGN.WHATSAPP.BULK.SCHEDULE_OPTIONAL')
                      }}</label
                    >
                    <ScheduledDateTimePicker
                      id="whatsapp-csv-scheduled-at"
                      v-model="scheduledAtCsv"
                      :min-date="minDateTime"
                    />
                  </div>
                  <button
                    class="flex h-[38px] items-center gap-2 rounded-lg bg-emerald-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 disabled:opacity-50"
                    :disabled="!selectedFile || isUploading"
                    @click="uploadCSV"
                  >
                    <span
                      v-if="isUploading"
                      class="icon i-lucide-loader animate-spin"
                    />
                    <span v-else class="icon i-lucide-send" />
                    {{
                      isUploading
                        ? scheduledAtCsv
                          ? t('CAMPAIGN.WHATSAPP.BULK.SCHEDULING')
                          : t('CAMPAIGN.WHATSAPP.BULK.PROCESSING')
                        : scheduledAtCsv
                          ? t('CAMPAIGN.WHATSAPP.BULK.SCHEDULE')
                          : t('CAMPAIGN.WHATSAPP.BULK.START')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Bulk Trigger Contact List Section -->
          <div
            v-if="selectedCampaign.status === 'approved'"
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-visible mb-6"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex items-center gap-3"
            >
              <div
                class="w-8 h-8 rounded bg-orange-100 dark:bg-orange-900/40 flex items-center justify-center"
              >
                <span
                  class="icon i-lucide-users text-orange-600 dark:text-orange-400"
                />
              </div>
              <div>
                <h3 class="font-medium text-slate-800 dark:text-slate-200">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.LABEL_TITLE') }}
                </h3>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.LABEL_DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="p-6">
              <div
                class="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-xl p-8 bg-slate-50 dark:bg-slate-800 transition-colors"
              >
                <div class="i-lucide-tags size-8 text-slate-400 mb-3" />
                <span
                  class="text-sm font-medium text-slate-700 dark:text-slate-300"
                  >{{ t('CAMPAIGN.WHATSAPP.BULK.SELECT_LABEL') }}</span
                >
                <div class="relative mt-4 w-64">
                  <select
                    id="whatsapp-contact-label"
                    v-model="selectedLabel"
                    :aria-label="t('CAMPAIGN.WHATSAPP.BULK.SELECT_LABEL')"
                    class="appearance-none bg-none block w-full reset-base !mb-0 border-none border-0 outline outline-1 outline-offset-[-1px] outline-slate-300 dark:outline-slate-600 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm rounded-lg pl-4 pr-10 py-[9px] focus:outline-blue-500 cursor-pointer"
                  >
                    <option value="" disabled>
                      {{ t('CAMPAIGN.WHATSAPP.BULK.CHOOSE_LABEL') }}
                    </option>
                    <option
                      v-for="label in labels"
                      :key="label.id"
                      :value="label.title"
                    >
                      {{ label.title }}
                    </option>
                  </select>
                  <div
                    class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none"
                  >
                    <div class="i-lucide-chevron-down size-4 text-slate-500" />
                  </div>
                </div>

                <span
                  v-if="labelMessage"
                  class="mt-4 text-xs font-medium"
                  :class="
                    labelMessage.includes('Erro')
                      ? 'text-red-500'
                      : 'text-green-500'
                  "
                  >{{ labelMessage }}</span
                >
              </div>
              <div class="relative mt-8 flex items-start justify-between gap-3">
                <div class="relative flex h-[38px] flex-col">
                  <label
                    for="whatsapp-label-scheduled-at"
                    class="text-xs text-slate-500 dark:text-slate-400 absolute -top-6 left-1 whitespace-nowrap"
                    >{{ t('CAMPAIGN.WHATSAPP.BULK.SCHEDULE_OPTIONAL') }}</label
                  >
                  <ScheduledDateTimePicker
                    id="whatsapp-label-scheduled-at"
                    v-model="scheduledAtLabel"
                    :min-date="minDateTime"
                  />
                </div>
                <button
                  class="flex h-[38px] items-center gap-2 rounded-lg bg-orange-600 px-5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-orange-700 disabled:opacity-50"
                  :disabled="!selectedLabel || isTriggeringLabel"
                  @click="triggerByLabel"
                >
                  <span
                    v-if="isTriggeringLabel"
                    class="icon i-lucide-loader animate-spin"
                  />
                  <span v-else class="icon i-lucide-send" />
                  {{
                    isTriggeringLabel
                      ? scheduledAtLabel
                        ? t('CAMPAIGN.WHATSAPP.BULK.SCHEDULING')
                        : t('CAMPAIGN.WHATSAPP.BULK.PROCESSING')
                      : scheduledAtLabel
                        ? t('CAMPAIGN.WHATSAPP.BULK.SCHEDULE')
                        : t('CAMPAIGN.WHATSAPP.BULK.START')
                  }}
                </button>
              </div>
            </div>
          </div>

          <!-- Tester Section -->
          <div
            v-if="selectedCampaign.status === 'approved'"
            class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden mb-8"
          >
            <div
              class="px-6 py-4 border-b border-slate-100 dark:border-slate-700/50 flex items-center gap-3"
            >
              <div
                class="w-8 h-8 rounded bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center"
              >
                <span
                  class="icon i-lucide-flask-conical text-purple-600 dark:text-purple-400"
                />
              </div>
              <div>
                <h3 class="font-medium text-slate-800 dark:text-slate-200">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.TEST_TITLE') }}
                </h3>
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  {{ t('CAMPAIGN.WHATSAPP.BULK.TEST_DESCRIPTION') }}
                </p>
              </div>
            </div>
            <div class="p-6">
              <div class="mb-5">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >{{ t('CAMPAIGN.WHATSAPP.BULK.DESTINATION') }}</label
                >
                <PhoneNumberInput
                  v-model="testPhoneNumber"
                  :aria-label="t('CAMPAIGN.WHATSAPP.BULK.DESTINATION')"
                  :placeholder="
                    t('CAMPAIGN.WHATSAPP.BULK.DESTINATION_PLACEHOLDER')
                  "
                  class="w-full max-w-sm"
                />
              </div>

              <label
                class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2"
                >{{ t('CAMPAIGN.WHATSAPP.BULK.VARIABLES') }}</label
              >
              <div
                v-if="testVariables.length === 0"
                class="text-sm text-slate-500 italic mb-6 mt-1"
              >
                {{ t('CAMPAIGN.WHATSAPP.BULK.NO_VARIABLES') }}
              </div>
              <div
                v-for="(variable, index) in testVariables"
                :key="index"
                class="flex gap-3 mb-3"
              >
                <input
                  v-model="variable.key"
                  type="text"
                  readonly
                  :aria-label="
                    t('CAMPAIGN.WHATSAPP.BULK.VARIABLE_KEY', {
                      name: variable.key,
                    })
                  "
                  class="flex-1 bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400 text-sm rounded-lg px-4 py-2.5 outline-none cursor-not-allowed"
                />
                <input
                  v-model="variable.value"
                  type="text"
                  :aria-label="
                    t('CAMPAIGN.WHATSAPP.BULK.VARIABLE_VALUE_LABEL', {
                      name: variable.key,
                    })
                  "
                  :placeholder="t('CAMPAIGN.WHATSAPP.BULK.VARIABLE_VALUE')"
                  class="flex-[2] bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div v-if="testVariables.length > 0" class="mb-4" />

              <div v-if="hasDynamicMedia" class="mb-6">
                <label
                  class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1"
                  >{{ t('CAMPAIGN.WHATSAPP.BULK.MEDIA_URL') }}</label
                >
                <input
                  v-model="testMediaUrl"
                  type="text"
                  :aria-label="t('CAMPAIGN.WHATSAPP.BULK.MEDIA_URL')"
                  :placeholder="t('CAMPAIGN.WHATSAPP.BULK.MEDIA_PLACEHOLDER')"
                  class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 text-sm rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div
                class="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-700/50"
              >
                <button
                  class="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50 flex items-center gap-2"
                  :disabled="isTesting || !testPhoneNumber"
                  @click="testWebhook"
                >
                  <span
                    v-if="isTesting"
                    class="icon i-lucide-loader animate-spin"
                  />
                  {{
                    isTesting
                      ? t('CAMPAIGN.WHATSAPP.BULK.SEND_TESTING')
                      : t('CAMPAIGN.WHATSAPP.BULK.SEND_TEST')
                  }}
                </button>
              </div>

              <div
                v-if="testResult"
                aria-live="polite"
                class="mt-6 p-4 bg-slate-900 rounded-lg shadow-inner"
              >
                <div
                  class="flex items-center gap-2 mb-2 text-slate-400 border-b border-slate-700 pb-2"
                >
                  <span class="icon i-lucide-terminal text-sm" />
                  <span
                    class="text-xs uppercase tracking-wider font-semibold"
                    >{{ t('CAMPAIGN.WHATSAPP.BULK.TERMINAL_OUTPUT') }}</span
                  >
                </div>
                <pre
                  class="text-sm text-green-400 whitespace-pre-wrap font-mono"
                  >{{ testResult }}</pre
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Create Modal -->
    <TemplateEditorDialog
      v-if="isCreating"
      :template="editingTemplate"
      @close="closeCreateModal"
    />
    <woot-delete-modal
      v-model:show="showDeleteModal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeleteCampaign"
      :title="t('CAMPAIGN.WHATSAPP.BULK.CONFIRM_ARCHIVE_TITLE')"
      :message="t('CAMPAIGN.WHATSAPP.BULK.CONFIRM_ARCHIVE')"
      :confirm-text="t('CAMPAIGN.WHATSAPP.BULK.CONFIRM')"
      :reject-text="t('CAMPAIGN.WHATSAPP.BULK.CANCEL')"
    />

    <div
      v-if="reviewingCampaign && isAdministrator"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="meta-review-title"
      @click.self="closeReview"
    >
      <div
        class="w-full max-w-lg rounded-xl bg-white p-6 shadow-xl dark:bg-slate-800"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="meta-review-title"
              class="text-lg font-semibold text-slate-900 dark:text-slate-100"
            >
              {{ t('CAMPAIGN.WHATSAPP.BULK.REVIEW_TITLE') }}
            </h2>
            <p class="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {{ reviewingCampaign.name }}
            </p>
          </div>
          <button
            type="button"
            class="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            :aria-label="t('CAMPAIGN.WHATSAPP.BULK.CANCEL')"
            @click="closeReview"
          >
            <span class="icon i-lucide-x" />
          </button>
        </div>

        <div
          class="mt-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <p class="font-semibold">
            {{ t('CAMPAIGN.WHATSAPP.BULK.REVIEW_WARNING') }}
          </p>
          <p class="mt-2">
            {{ t('CAMPAIGN.WHATSAPP.BULK.REVIEW_DESCRIPTION') }}
          </p>
          <dl class="mt-3 space-y-1 text-xs">
            <div class="flex justify-between gap-4">
              <dt>{{ t('CAMPAIGN.WHATSAPP.BULK.SUBMITTED_CATEGORY') }}</dt>
              <dd class="font-semibold">
                {{
                  reviewingCampaign.variables?.meta_submitted_category || '—'
                }}
              </dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt>{{ t('CAMPAIGN.WHATSAPP.BULK.META_CATEGORY') }}</dt>
              <dd class="font-semibold">
                {{
                  reviewingCampaign.variables?.meta_category ||
                  t('CAMPAIGN.WHATSAPP.BULK.MARKETING')
                }}
              </dd>
            </div>
          </dl>
        </div>

        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-700 dark:text-red-300 dark:hover:bg-red-950/30"
            @click="removeReview"
          >
            {{ t('CAMPAIGN.WHATSAPP.BULK.DELETE_MODEL') }}
          </button>
          <button
            type="button"
            class="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
            @click="releaseReview"
          >
            {{ t('CAMPAIGN.WHATSAPP.BULK.RELEASE') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
